import React, { useContext, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Cookies from "js-cookie";
import { AccountContext } from "../account/account";
import classNames from "classnames";

/**
 * Reusable Banner Component
 * Displays banners from the API with support for dismissable banners using cookies
 *
 * @param {Object} banner - Banner object from API
 * @param {string} banner.banner_id - Unique banner ID
 * @param {string} banner.banner_text - Text to display (supports HTML/rich text)
 * @param {string} banner.banner_color - Background color (hex or rgba)
 * @param {string} banner.text_color - Text color (hex or rgba)
 * @param {string} banner.text_alignment - Text alignment (left, center, right, justify)
 * @param {Object} banner.text_margin - Text margin object {top, right, bottom, left}
 * @param {string} banner.button_text - Button text (optional)
 * @param {string} banner.button_url - Button URL (optional)
 * @param {string} banner.button_color - Button background color (hex or rgba, optional)
 * @param {string} banner.button_text_color - Button text color (hex or rgba, optional)
 * @param {Object} banner.button_margin - Button margin object {top, right, bottom, left}
 * @param {boolean} banner.is_dismissable - Whether banner can be dismissed
 * @param {number} banner.dismiss_days - Days until cookie expires (default: 7)
 * @param {string} banner.dismiss_icon_color - Color of dismiss icon (hex or rgba, optional)
 */
const Banner = ({ banner }) => {
  const { userAttributes } = useContext(AccountContext) ?? {};
  const [isDismissed, setIsDismissed] = useState(false);

  if (!banner || !banner.banner_id) {
    return null;
  }

  const cookieKey = `grigora-banner-dismissed-${
    userAttributes?.email || "anonymous"
  }-${banner.banner_id}`;

  useEffect(() => {
    // Check if banner is dismissed via cookie
    if (banner.is_dismissable) {
      const dismissed = Cookies.get(cookieKey) === "true";
      setIsDismissed(dismissed);
    }
  }, [banner.is_dismissable, cookieKey]);

  const handleDismiss = () => {
    if (banner.is_dismissable) {
      const expiryDays = banner.dismiss_days || 7;
      Cookies.set(cookieKey, "true", { expires: expiryDays });
      setIsDismissed(true);
    }
  };

  // Don't render if dismissed
  if (isDismissed) {
    return null;
  }

  // Get text alignment class
  const getTextAlignmentClass = (alignment) => {
    switch (alignment) {
      case "center":
        return "text-center";
      case "right":
        return "text-right";
      case "justify":
        return "text-justify";
      default:
        return "text-left";
    }
  };

  // Get text margin styles (only for desktop)
  const getTextMarginStyle = () => {
    if (!banner.text_margin) {
      return {};
    }
    const margin =
      typeof banner.text_margin === "object"
        ? banner.text_margin
        : { top: 0, right: 0, bottom: 0, left: 0 };
    // Return CSS custom properties that will be applied via media query
    return {
      "--text-margin-top": `${margin.top || 0}px`,
      "--text-margin-right": `${margin.right || 0}px`,
      "--text-margin-bottom": `${margin.bottom || 0}px`,
      "--text-margin-left": `${margin.left || 0}px`,
    };
  };

  // Get button margin styles (only for desktop)
  const getButtonMarginStyle = () => {
    if (!banner.button_margin) {
      return {};
    }
    const margin =
      typeof banner.button_margin === "object"
        ? banner.button_margin
        : { top: 0, right: 0, bottom: 0, left: 0 };
    // Return CSS custom properties that will be applied via media query
    return {
      "--button-margin-top": `${margin.top || 0}px`,
      "--button-margin-right": `${margin.right || 0}px`,
      "--button-margin-bottom": `${margin.bottom || 0}px`,
      "--button-margin-left": `${margin.left || 0}px`,
    };
  };

  // Get button padding styles
  const getButtonPaddingStyle = () => {
    if (!banner.button_padding) {
      return {};
    }
    const padding =
      typeof banner.button_padding === "object"
        ? banner.button_padding
        : { top: 0, right: 0, bottom: 0, left: 0 };
    return {
      paddingTop: `${padding.top || 0}px`,
      paddingRight: `${padding.right || 0}px`,
      paddingBottom: `${padding.bottom || 0}px`,
      paddingLeft: `${padding.left || 0}px`,
    };
  };

  // Get secondary button margin styles (only for desktop)
  const getSecondaryButtonMarginStyle = () => {
    if (!banner.secondary_button_margin) {
      return {};
    }
    const margin =
      typeof banner.secondary_button_margin === "object"
        ? banner.secondary_button_margin
        : { top: 0, right: 0, bottom: 0, left: 0 };
    return {
      "--secondary-button-margin-top": `${margin.top || 0}px`,
      "--secondary-button-margin-right": `${margin.right || 0}px`,
      "--secondary-button-margin-bottom": `${margin.bottom || 0}px`,
      "--secondary-button-margin-left": `${margin.left || 0}px`,
    };
  };

  // Get secondary button padding styles
  const getSecondaryButtonPaddingStyle = () => {
    if (!banner.secondary_button_padding) {
      return {};
    }
    const padding =
      typeof banner.secondary_button_padding === "object"
        ? banner.secondary_button_padding
        : { top: 0, right: 0, bottom: 0, left: 0 };
    return {
      paddingTop: `${padding.top || 0}px`,
      paddingRight: `${padding.right || 0}px`,
      paddingBottom: `${padding.bottom || 0}px`,
      paddingLeft: `${padding.left || 0}px`,
    };
  };

  // Get layout classes
  const getLayoutClasses = () => {
    const layout = banner.layout || "text-left-button-right";
    switch (layout) {
      case "text-left-button-right":
        return "flex-row justify-between";
      case "text-center-button-right":
        return "flex-row justify-between";
      case "text-center-button-center":
        return "flex-row justify-center items-center";
      case "text-center-button-left":
        return "flex-row items-center";
      case "text-center-button-center-left":
        return "flex-row-reverse justify-center items-center";
      case "text-center-sideways":
        return "flex-row justify-between items-center";
      default:
        return "flex-row justify-between";
    }
  };

  const getButtonContainerClasses = () => {
    const layout = banner.layout || "text-left-button-right";
    switch (layout) {
      case "text-center-button-center":
        return "flex flex-row gap-2 items-center";
      case "text-center-button-left":
        return "flex flex-row gap-2 items-center";
      case "text-center-button-center-left":
        return "flex flex-row gap-2 items-center";
      case "text-center-sideways":
        return "flex flex-row gap-2 items-center";
      default:
        return "flex flex-row gap-2 items-center";
    }
  };

  const bannerStyle = {
    backgroundColor: banner.banner_color || "#5CBD20",
    fontFamily: banner.font_family || "Inter",
  };

  const textStyle = {
    color: banner.text_color || "#FFFFFF",
    fontSize: banner.text_font_size ? `${banner.text_font_size}px` : "14px",
    ...getTextMarginStyle(),
  };

  const getButtonStyleWithLayout = (baseStyle, buttonLayout, buttonColor) => {
    if (!baseStyle || Object.keys(baseStyle).length === 0) return {};

    const style = { ...baseStyle };
    switch (buttonLayout) {
      case "filled":
        // Keep background color as is, ensure border is none
        style.border = "none";
        break;
      case "outline":
        // Only border, no background
        style.backgroundColor = "transparent";
        style.border = `1px solid ${buttonColor || "#FFFFFF"}`;
        break;
      case "text":
        // No background, no border
        style.backgroundColor = "transparent";
        style.border = "none";
        style.textDecoration = "underline";
        break;
      default:
        style.border = "none";
        break;
    }
    return style;
  };

  const buttonStyle = banner.button_text
    ? getButtonStyleWithLayout(
        {
          backgroundColor: banner.button_color || "#FFFFFF",
          color: banner.button_text_color || "#000000",
          fontSize: banner.button_font_size
            ? `${banner.button_font_size}px`
            : "14px",
          ...getButtonMarginStyle(),
          ...getButtonPaddingStyle(),
        },
        banner.button_layout || "filled",
        banner.button_color || "#FFFFFF",
      )
    : {};

  const secondaryButtonStyle =
    banner.secondary_button_enabled && banner.secondary_button_text
      ? getButtonStyleWithLayout(
          {
            backgroundColor: banner.secondary_button_color || "#FFFFFF",
            color: banner.secondary_button_text_color || "#000000",
            fontSize: banner.button_font_size
              ? `${banner.button_font_size}px`
              : "14px",
            ...getSecondaryButtonMarginStyle(),
            ...getSecondaryButtonPaddingStyle(),
          },
          banner.secondary_button_layout || "filled",
          banner.secondary_button_color || "#FFFFFF",
        )
      : {};

  // Generate unique ID for this banner instance to scope the styles
  const bannerStyleId = `banner-${banner.banner_id}`;

  const dismissIconColor =
    banner.dismiss_icon_color || banner.text_color || "#FFFFFF";

  const renderButton = () => {
    if (!banner.button_text || !banner.button_url) {
      return null;
    }

    const isExternalLink =
      banner.button_url.startsWith("http://") ||
      banner.button_url.startsWith("https://");

    if (isExternalLink) {
      return (
        <a
          href={banner.button_url}
          target="_blank"
          rel="noopener noreferrer"
          className={`px-4 py-2 rounded-[8px] font-medium transition-opacity hover:opacity-90 whitespace-nowrap ${bannerStyleId}-button`}
          style={buttonStyle}
        >
          {banner.button_text}
        </a>
      );
    }

    return (
      <Link
        to={banner.button_url}
        className={`px-4 py-2 rounded-[8px] font-medium transition-opacity hover:opacity-90 whitespace-nowrap ${bannerStyleId}-button`}
        style={buttonStyle}
      >
        {banner.button_text}
      </Link>
    );
  };

  const renderSecondaryButton = () => {
    if (
      !banner.secondary_button_enabled ||
      !banner.secondary_button_text ||
      !banner.secondary_button_url
    ) {
      return null;
    }

    const isExternalLink =
      banner.secondary_button_url.startsWith("http://") ||
      banner.secondary_button_url.startsWith("https://");

    if (isExternalLink) {
      return (
        <a
          href={banner.secondary_button_url}
          target="_blank"
          rel="noopener noreferrer"
          className={`px-4 py-2 rounded-[8px] font-medium transition-opacity hover:opacity-90 whitespace-nowrap ${bannerStyleId}-secondary-button`}
          style={secondaryButtonStyle}
        >
          {banner.secondary_button_text}
        </a>
      );
    }

    return (
      <Link
        to={banner.secondary_button_url}
        className={`px-4 py-2 rounded-[8px] font-medium transition-opacity hover:opacity-90 whitespace-nowrap ${bannerStyleId}-secondary-button`}
        style={secondaryButtonStyle}
      >
        {banner.secondary_button_text}
      </Link>
    );
  };

  return (
    <>
      {/* Inject styles for desktop-only margins */}
      <style>
        {`
          @media (min-width: 768px) {
            .${bannerStyleId}-text {
              margin-top: var(--text-margin-top, 0);
              margin-right: var(--text-margin-right, 0);
              margin-bottom: var(--text-margin-bottom, 0);
              margin-left: var(--text-margin-left, 0);
            }
            .${bannerStyleId}-button {
              margin-top: var(--button-margin-top, 0);
              margin-right: var(--button-margin-right, 0);
              margin-bottom: var(--button-margin-bottom, 0);
              margin-left: var(--button-margin-left, 0);
            }
            .${bannerStyleId}-secondary-button {
              margin-top: var(--secondary-button-margin-top, 0);
              margin-right: var(--secondary-button-margin-right, 0);
              margin-bottom: var(--secondary-button-margin-bottom, 0);
              margin-left: var(--secondary-button-margin-left, 0);
            }
          }
        `}
      </style>
      <div
        className={classNames(
          "w-full py-[4px] flex items-center",
          getLayoutClasses(),
          "relative z-50",
        )}
        style={{
          ...bannerStyle,
          gap: banner.gap ? `${banner.gap}px` : "16px",
          paddingLeft: "16px",
          paddingRight: banner.is_dismissable ? "calc(16px + 40px)" : "16px",
        }}
      >
        {banner.layout === "text-center-button-left" && (
          <div className={getButtonContainerClasses()}>
            {renderButton()}
            {renderSecondaryButton()}
          </div>
        )}
        {banner.layout === "text-center-sideways" && (
          <div className={getButtonContainerClasses()}>
            {renderSecondaryButton()}
          </div>
        )}
        <div
          className={classNames(
            "font-medium",
            banner.layout === "text-center-button-center" ||
              banner.layout === "text-center-sideways" ||
              banner.layout === "text-center-button-center-left"
              ? ""
              : "flex-1",
            getTextAlignmentClass(banner.text_alignment || "left"),
            `${bannerStyleId}-text`,
          )}
          style={textStyle}
          dangerouslySetInnerHTML={{ __html: banner.banner_text || "" }}
        />
        {(banner.layout === "text-center-button-center" ||
          banner.layout === "text-center-button-center-left") && (
          <div className={getButtonContainerClasses()}>
            {renderButton()}
            {renderSecondaryButton()}
          </div>
        )}
        {banner.layout !== "text-center-button-left" &&
          banner.layout !== "text-center-button-center" &&
          banner.layout !== "text-center-button-center-left" &&
          banner.layout !== "text-center-sideways" && (
            <div className={getButtonContainerClasses()}>
              {renderButton()}
              {renderSecondaryButton()}
            </div>
          )}
        {banner.layout === "text-center-sideways" && (
          <div className={getButtonContainerClasses()}>{renderButton()}</div>
        )}
        {banner.is_dismissable && (
          <button
            onClick={handleDismiss}
            className="absolute right-4 top-1/2 -translate-y-1/2 px-3 flex py-[4px] text-sm hover:bg-black/20 rounded transition-colors z-10"
            aria-label="Dismiss banner"
            style={{ color: dismissIconColor }}
          >
            ×
          </button>
        )}
      </div>
    </>
  );
};

/**
 * BannerContainer Component
 * Renders all eligible banners from userData
 * Should be used inside GrigoraAccountContext
 */
const BannerContainer = ({ userData }) => {
  if (
    !userData?.banners ||
    !Array.isArray(userData.banners) ||
    userData.banners.length === 0
  ) {
    return null;
  }

  return (
    <div className="w-full flex flex-col">
      {userData.banners.map((banner) => (
        <Banner key={banner.banner_id} banner={banner} />
      ))}
    </div>
  );
};

export default Banner;
export { Banner, BannerContainer };
